/**
 * Available tab — the catalog of source types from GET /integration/catalog.
 * `available` types offer Connect (→ pre-scoped create form); `coming_soon`
 * types render an inert "Coming soon" card.
 */

import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Group, Loader, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconPlugConnected } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listCatalog, type CatalogEntry } from "@/shared/api/integration";
import { SourceTile } from "./SourceTile";
import { sourceVisual } from "./sourceVisuals";

export function AvailableList() {
  const getToken = useAccessToken();
  const [items, setItems] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await listCatalog(token);
      setItems(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load the catalog">
        {error}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" verticalSpacing="lg">
        {items.map((entry) => {
          const available = entry.status === "available";
          const { color } = sourceVisual(entry.source_type);
          return (
            <Card
              key={entry.source_type}
              withBorder
              radius="md"
              padding="lg"
              style={{ minHeight: 220, opacity: available ? 1 : 0.85 }}
            >
              <Stack gap="sm" h="100%">
                <Group gap="sm" wrap="nowrap" align="center">
                  <SourceTile sourceType={entry.source_type} />
                  <Text fw={600} size="lg">
                    {entry.display_name}
                  </Text>
                </Group>

                <Group gap={6}>
                  {entry.categories.map((c) => (
                    <Badge key={c} variant="light" color={color} size="sm" radius="sm">
                      {c}
                    </Badge>
                  ))}
                </Group>

                <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                  {entry.description}
                </Text>

                {available ? (
                  <Button
                    component={RouterLink}
                    to={`/integration/connections/new?source=${encodeURIComponent(entry.source_type)}`}
                    leftSection={<IconPlugConnected size={16} />}
                    color={color}
                    variant="light"
                    fullWidth
                  >
                    Connect
                  </Button>
                ) : (
                  <Button variant="default" fullWidth disabled>
                    Coming soon
                  </Button>
                )}
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>
      <Text size="xs" c="dimmed">
        Logos are trademarks of their respective owners, shown for identification only.
      </Text>
    </Stack>
  );
}
